import dayjs, { Dayjs, extend } from "dayjs";
import utc from "dayjs/plugin/utc";
import mergeWith from "lodash/mergeWith";

import { ZoneTZIndependant } from "../../../../../services/TimeService/TimeService";

export type DisabledTimes = {
    disabledHours?: () => number[];
    disabledMinutes?: (hour: number) => number[];
    disabledSeconds?: (hour: number, minute: number) => number[];
};
export type DisabledTime<DateType> = (date: DateType | null) => DisabledTimes;

export interface DateInterval {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
}

extend(utc);

type Minute = number;
type Hour = number;
type Day = string;

type Minutes = {
    minutes: Minute[];
};

type HourMinutes = Record<Hour, Minutes>;

export type DateHourMinutes = Record<Day, HourMinutes>;
/*
const dateHourMinutes: DateHourMinutes = {
    "2023-04-05": {
        2: {
            minutes: [2, 3, 4],
        },
    },
    "2023-04-06": {
        5: {
            minutes: [...range(0, 21), ...range(30, 39)],
        },
    },
};
*/

const range = (start: number, end: number) => {
    const result = [];
    for (let i = start; i < end; i++) {
        result.push(i);
    }
    return result;
};

function convertToDayjs(tz: ZoneTZIndependant) {
    return function convert(dateInverval: DateInterval): { startDate: Dayjs; endDate: Dayjs } {
        return {
            startDate: dayjs(dateInverval.startDate).tz(tz),
            endDate: dayjs(dateInverval.endDate).tz(tz),
        };
    };
}

/*
split dateInterval when start and date have different hours
convert interval { startDate: 12:10, endDate: 13:20 } in
in 2 intervals [ { startDate: 12:10, endDate: 12:59 },
                 { startDate: 13:00, endDate: 13:20 }]
*/
function splitDate(dateInterval: { startDate: Dayjs; endDate: Dayjs }): { startDate: Dayjs; endDate: Dayjs }[] {
    const { startDate, endDate } = dateInterval;

    const startHour = startDate.hour();
    const endHour = endDate.hour();
    if (startHour != endHour) {
        const start = startDate.startOf("hour");
        const end = startDate.endOf("hour");
        // hours is float.
        // i.e. 0.15 from 12:55 to 13:05 and 1.3 from 12:20 to 14:45
        const hours = endDate.diff(startDate, "hours", true);
        // if hours is less than 2, we need 2 intervals
        // else we want to have ceil(hours) intervals
        const intervalLength = hours < 2 ? 2 : Math.ceil(hours);
        const intervals = Array.from({ length: intervalLength }, (_, idx) => idx).map((idx) => ({
            startDate: idx === 0 ? startDate : start.add(idx, "hour"),
            endDate: idx === intervalLength - 1 ? endDate : end.add(idx, "hour"),
        }));
        return intervals;
    }
    return [{ startDate, endDate }];
}

export function disabledDateTime(blockedDates: Array<DateInterval>, tz: ZoneTZIndependant) {
    return function transformBlockedDates(date: Dayjs | null): DisabledTimes {
        const disabledTimes: DateHourMinutes = blockedDates
            .map(convertToDayjs(tz))
            .map(splitDate)
            .flat()
            .map(({ startDate, endDate }) => {
                const start = startDate.minute();
                const count = endDate.diff(startDate, "minute") + 1;
                const minutes: Array<number> = range(start, start + count);
                const day = startDate.startOf("day").toISOString();
                return {
                    day,
                    [startDate.hour()]: { minutes, startDate, endDate, count },
                };
            })
            .reduce(
                (acc, current) => {
                    const { day } = current;
                    acc[day] = mergeWith(acc[day], current, mergeArrays);
                    return acc;
                },
                {} as Record<string, any>
            );
        return {
            disabledHours: () => {
                if (date == null) {
                    return [];
                }
                const startDate = dayjs(date).startOf("day").toISOString() || "";
                const hours = Object.entries(disabledTimes[startDate] || {})
                    .filter(([_hour, { minutes }]) => minutes?.length === 60)
                    .map(([hour, _]) => parseInt(hour, 10));
                return hours;
            },
            disabledMinutes: (hour: number) => {
                if (date == null) {
                    return [];
                }
                const startDate = dayjs(date).startOf("day").toISOString() || "";
                return disabledTimes[startDate]?.[hour]?.minutes || [];
            },
        };
    };
}

export function overlaps({
    blockedDates,
    startDate,
    endDate,
    scheduledJourneyStartDateTimezone,
}: {
    blockedDates: any;
    startDate: Dayjs;
    endDate: Dayjs;
    scheduledJourneyStartDateTimezone: any;
}): boolean | undefined {
    return splitDate({ startDate, endDate }).some((date) =>
        overlapsInternal({ blockedDates, ...date, scheduledJourneyStartDateTimezone })
    );
}

function overlapsInternal({
    blockedDates,
    startDate,
    endDate,
    scheduledJourneyStartDateTimezone,
}: {
    blockedDates: any;
    startDate: Dayjs;
    endDate: Dayjs;
    scheduledJourneyStartDateTimezone: any;
}): boolean | undefined {
    const hour = startDate.hour();
    const journeyScheduledStartMinute = startDate.minute();
    const journeyScheduledEndMinute = endDate.minute();

    const disabledMinutes = disabledDateTime(
        blockedDates,
        scheduledJourneyStartDateTimezone
    )(startDate).disabledMinutes?.(hour);

    return disabledMinutes?.some(
        (minute) => journeyScheduledStartMinute < minute && minute < journeyScheduledEndMinute
    );
}

function mergeArrays(objValue: any, srcValue: any) {
    if (Array.isArray(objValue)) {
        return objValue.concat(srcValue);
    }
}

export function disabledDate(current: Dayjs): boolean {
    // Can not select past dates
    const isDisabled = current && current < dayjs().startOf("day");
    return isDisabled;
}
